// src/components/legal/DocumentPreviewModal.tsx
// ---------------------------------------------------------------------------
// Modal for previewing, downloading, and signing legal documents.
// Matches the glassmorphic design of LegalAcceptanceModal.
// ---------------------------------------------------------------------------

import {
  Close as CloseIcon,
  Download as DownloadIcon,
  Gavel as LegalIcon,
  KeyboardArrowDown as ScrollIcon,
  Language as LanguageIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "../../i18n";
import { athenaColors } from "../../theme/palette";
import { glass } from "../../theme/glassTokens";
import api from "../../api/api";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type DocumentPreviewData = {
  key: string;
  title: string;
  description: string;
  category: string;
  languages: string[];
  requires_signature: boolean;
  enabled: boolean;
};

type DocumentPreviewModalProps = {
  open: boolean;
  document: DocumentPreviewData | null;
  onClose: () => void;
  /** Called after a successful signing action */
  onSigned?: () => void;
};

// ---------------------------------------------------------------------------
// Markdown → HTML renderer (same as LegalAcceptanceModal)
// ---------------------------------------------------------------------------

function renderMarkdownToHtml(md: string): string {
  return md
    // h4
    .replace(
      /^#### (.+)$/gm,
      '<h4 style="margin:16px 0 6px;font-size:0.95rem;font-weight:600;letter-spacing:0.02em">$1</h4>',
    )
    // h3
    .replace(
      /^### (.+)$/gm,
      '<h3 style="margin:24px 0 8px;font-size:1.05rem;font-weight:600;letter-spacing:0.02em">$1</h3>',
    )
    // h2
    .replace(
      /^## (.+)$/gm,
      '<h2 style="margin:32px 0 12px;font-size:1.25rem;font-weight:700;letter-spacing:0.01em">$1</h2>',
    )
    // h1
    .replace(
      /^# (.+)$/gm,
      '<h1 style="margin:32px 0 16px;font-size:1.4rem;font-weight:800;letter-spacing:0.01em">$1</h1>',
    )
    // Bold
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    // Italic
    .replace(/\*(.+?)\*/g, "<em>$1</em>")
    // Horizontal rule
    .replace(/^---$/gm, '<hr style="margin:24px 0;border:none;border-top:1px solid #c5cce0" />')
    // Unordered list items
    .replace(/^- (.+)$/gm, '<li style="margin-bottom:4px;line-height:1.7">$1</li>')
    // Wrap consecutive <li> items in <ul>
    .replace(
      /(<li[^>]*>.*?<\/li>\n?)+/g,
      '<ul style="padding-left:24px;margin:8px 0">$&</ul>',
    )
    // Paragraphs
    .replace(
      /^(?!<[a-z])([\w\d\[].+)$/gm,
      '<p style="margin:8px 0;line-height:1.75">$1</p>',
    );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function DocumentPreviewModal({
  open,
  document: doc,
  onClose,
  onSigned,
}: DocumentPreviewModalProps) {
  const theme = useTheme();
  const { t } = useTranslation();

  // Content state
  const [content, setContent] = useState<string | null>(null);
  const [loadingContent, setLoadingContent] = useState(false);
  const [contentLang, setContentLang] = useState("en");
  const [availableLanguages, setAvailableLanguages] = useState<string[]>([]);
  const [contentError, setContentError] = useState<string | null>(null);

  // Download state
  const [downloading, setDownloading] = useState(false);

  // Signing state
  const [showSignature, setShowSignature] = useState(false);
  const [signing, setSigning] = useState(false);
  const [signError, setSignError] = useState<string | null>(null);
  const [signed, setSigned] = useState(false);
  const [typedName, setTypedName] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [emailHint, setEmailHint] = useState("");
  const [sendingOtp, setSendingOtp] = useState(false);

  // Scroll tracking
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  // -----------------------------------------------------------------------
  // Fetch markdown content when doc or language changes
  // -----------------------------------------------------------------------
  useEffect(() => {
    if (!open || !doc) {
      setContent(null);
      setContentError(null);
      setShowSignature(false);
      setSigned(false);
      setHasScrolledToBottom(false);
      return;
    }

    let cancelled = false;
    (async () => {
      setLoadingContent(true);
      setContentError(null);
      try {
        const resp = await api.get(
          `/billing/legal/library/${doc.key}/content/?lang=${contentLang}`,
        );
        if (cancelled) return;
        setContent(resp.data.content);
        setAvailableLanguages(resp.data.available_languages || []);
      } catch {
        if (cancelled) return;
        setContent(null);
        setContentError(
          t(
            "billing.legal.content_unavailable",
            "Document preview is not available. You can still download the PDF.",
          ),
        );
      } finally {
        if (!cancelled) setLoadingContent(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, doc, contentLang, t]);

  // Reset scroll state when content changes
  useEffect(() => {
    setHasScrolledToBottom(false);
    const el = contentRef.current;
    if (el) {
      el.scrollTop = 0;
      requestAnimationFrame(() => {
        if (el.scrollHeight <= el.clientHeight + 30) {
          setHasScrolledToBottom(true);
        }
      });
    }
  }, [content]);

  // -----------------------------------------------------------------------
  // Scroll detection
  // -----------------------------------------------------------------------
  const handleScroll = useCallback(() => {
    const el = contentRef.current;
    if (!el) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 30) {
      setHasScrolledToBottom(true);
    }
  }, []);

  // -----------------------------------------------------------------------
  // Download handler
  // -----------------------------------------------------------------------
  const handleDownload = async () => {
    if (!doc) return;
    setDownloading(true);
    try {
      const resp = await api.get(`/billing/legal/library/${doc.key}/download/?output=pdf`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(resp.data as Blob);
      const a = window.document.createElement("a");
      a.href = url;
      a.download = `${doc.key}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setDownloading(false);
    }
  };

  const handleRequestOtp = async () => {
    setSendingOtp(true);
    setSignError(null);
    try {
      const res = await api.post("/billing/legal/agreement/request-otp/");
      setOtpSent(true);
      setEmailHint(res.data.email_hint || "");
    } catch (e: any) {
      setSignError(e?.response?.data?.detail || t("billing.legal.otp_error", "Failed to send code"));
    } finally {
      setSendingOtp(false);
    }
  };

  const handleSign = async () => {
    if (!doc || !otpCode || !typedName) return;
    setSigning(true);
    setSignError(null);
    try {
      const docsResp = await api.get(`/billing/legal/documents/`);
      const legalDocs: { id: string; document_type: string }[] = docsResp.data;
      const matchingDoc = legalDocs.find(
        (d) =>
          d.document_type === doc.key ||
          d.document_type.replace(/_/g, "-") === doc.key.replace(/_/g, "-"),
      );

      if (!matchingDoc) {
        setSignError(
          t(
            "billing.legal.sign_no_doc",
            "No matching legal document found for signing. Please contact support.",
          ),
        );
        return;
      }

      const createResp = await api.post("/billing/legal/institution/agreements/", {
        document_id: matchingDoc.id,
        placeholder_values: {},
      });
      const agreementId = createResp.data.id;

      await api.post(`/billing/legal/institution/agreements/${agreementId}/sign/`, {
        otp_code: otpCode,
        confirm_read: true,
        confirm_authorized: true,
        signer_name: typedName,
      });

      setSigned(true);
      onSigned?.();
    } catch (e: any) {
      const msg = e?.response?.data?.detail || e?.response?.data?.message;
      setSignError(
        msg || t("billing.legal.sign_error", "Failed to sign. Please try again."),
      );
    } finally {
      setSigning(false);
    }
  };

  if (!doc) return null;

  // -----------------------------------------------------------------------
  // Language label helper
  // -----------------------------------------------------------------------
  const langLabel = (code: string) => {
    switch (code) {
      case "en":
        return "English";
      case "es":
        return "Espanol";
      case "ca":
        return "Catala";
      default:
        return code.toUpperCase();
    }
  };

  return (
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth
      onClose={onClose}
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: "blur(8px)",
            backgroundColor: alpha(theme.palette.common.black, 0.6),
          },
        },
      }}
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
          background: `linear-gradient(160deg, ${athenaColors.deepNavy} 0%, ${alpha(
            athenaColors.royalBlue,
            0.95
          )} 100%)`,
          backdropFilter: "blur(16px)",
          border: `1px solid ${alpha(athenaColors.periwinkle, 0.3)}`,
          boxShadow: `0 24px 80px ${alpha(
            athenaColors.deepNavy,
            0.5
          )}, 0 0 30px ${alpha(athenaColors.royalBlue, 0.2)}`,
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* ================================================================= */}
      {/* HEADER                                                            */}
      {/* ================================================================= */}
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          pb: 1.5,
          background: alpha(athenaColors.deepNavy, 0.5),
          borderBottom: `1px solid ${alpha(athenaColors.periwinkle, 0.2)}`,
        }}
      >
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: alpha(athenaColors.periwinkle, 0.15),
            border: `1px solid ${alpha(athenaColors.periwinkle, 0.25)}`,
          }}
        >
          <LegalIcon sx={{ color: athenaColors.periwinkle, fontSize: 24 }} />
        </Box>

        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2, color: theme.palette.common.white }}>
            {doc.title}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.25, color: athenaColors.periwinkle }}>
            {doc.description}
          </Typography>
        </Box>

        {/* Language selector chip */}
        {availableLanguages.length > 1 && (
          <TextField
            select
            size="small"
            value={contentLang}
            onChange={(e) => setContentLang(e.target.value)}
            variant="outlined"
            sx={{
              minWidth: 120,
              "& .MuiOutlinedInput-root": {
                color: theme.palette.common.white,
                borderColor: alpha(athenaColors.periwinkle, 0.4),
                borderRadius: 2,
                fontSize: "0.82rem",
                fontWeight: 600,
                "& fieldset": {
                  borderColor: alpha(athenaColors.periwinkle, 0.4),
                },
                "&:hover fieldset": {
                  borderColor: athenaColors.periwinkle,
                },
                "&.Mui-focused fieldset": {
                  borderColor: athenaColors.periwinkle,
                },
              },
              "& .MuiSvgIcon-root": {
                color: athenaColors.periwinkle,
              },
            }}
          >
            {availableLanguages.map((lng) => (
              <MenuItem key={lng} value={lng}>
                {langLabel(lng)}
              </MenuItem>
            ))}
          </TextField>
        )}

        {/* Close button */}
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: athenaColors.periwinkle,
            "&:hover": { color: theme.palette.common.white, background: alpha(athenaColors.periwinkle, 0.2) },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      {/* ================================================================= */}
      {/* METADATA BAND                                                     */}
      {/* ================================================================= */}
      <Box
        sx={{
          px: 3,
          py: 1,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          background: glass.panelSunken,
          borderBottom: `1px solid ${glass.headerDivider}`,
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1 }}>
          <LanguageIcon sx={{ fontSize: 16, color: glass.textSecondary }} />
          <Typography variant="caption" sx={{ color: glass.textSecondary, fontWeight: 600 }}>
            {langLabel(contentLang).toUpperCase()}
          </Typography>

          {doc.requires_signature && (
            <Chip
              size="small"
              label={
                signed
                  ? t("billing.legal.signed", "Signed")
                  : t("billing.legal.requires_signature", "Requires signature")
              }
              variant="outlined"
              sx={{
                height: 22,
                fontSize: "0.7rem",
                fontWeight: 700,
                borderColor: signed
                  ? alpha(theme.palette.success.light, 0.5)
                  : alpha(theme.palette.warning.main, 0.5),
                color: signed
                  ? theme.palette.success.light
                  : theme.palette.warning.main,
              }}
            />
          )}
        </Stack>

        <Typography variant="caption" sx={{ color: glass.textSecondary }}>
          {doc.category.charAt(0).toUpperCase() + doc.category.slice(1)}
        </Typography>
      </Box>

      {/* ================================================================= */}
      {/* SCROLLABLE CONTENT                                                */}
      {/* ================================================================= */}
      <DialogContent
        ref={contentRef}
        onScroll={handleScroll}
        sx={{
          flex: 1,
          overflowY: "auto",
          px: 3,
          py: 2,
          background: "transparent",
          "&::-webkit-scrollbar": { width: 6 },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: alpha(athenaColors.white, 0.25),
            borderRadius: 3,
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "transparent",
          },
        }}
      >
        {loadingContent ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress sx={{ color: glass.accent }} />
          </Box>
        ) : contentError ? (
          <Box sx={{ py: 4, textAlign: "center" }}>
            <Typography variant="body1" sx={{ color: glass.textSecondary, mb: 2 }}>
              {contentError}
            </Typography>
          </Box>
        ) : content ? (
          <>
            <Box
              dangerouslySetInnerHTML={{
                __html: renderMarkdownToHtml(content),
              }}
              sx={{
                color: glass.textPrimary,
                fontSize: "0.92rem",
                lineHeight: 1.75,
                // renderMarkdownToHtml emits no colours, so these rules are the
                // only thing inking the document.
                "& h1": { color: glass.textPrimary },
                "& h2": { color: glass.accent },
                "& h3": { color: glass.textPrimary },
                "& h4": { color: glass.textSecondary },
                "& ul": { color: glass.textSecondary },
                "& p": { color: glass.textPrimary },
                "& strong": { color: glass.textPrimary },
              }}
            />

            {showSignature && doc.requires_signature && !signed && (
              <Box
                sx={{
                  mt: 4,
                  p: 2.5,
                  borderRadius: 2,
                  border: `1px solid ${alpha(athenaColors.periwinkle, 0.4)}`,
                  background: alpha(athenaColors.periwinkle, 0.08),
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 800, mb: 2, color: glass.textPrimary }}
                >
                  {t("billing.legal.sign_section_title", "Sign This Document")}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ mb: 2, color: glass.textSecondary }}
                >
                  {t(
                    "billing.legal.sign_section_desc_otp",
                    "Type your full name below and verify via email OTP to sign electronically. This constitutes a Simple Electronic Signature (SES) in accordance with Regulation (EU) No 910/2014 (eIDAS).",
                  )}
                </Typography>

                <TextField
                  fullWidth
                  size="small"
                  label={t("billing.legal.typed_name", "Type your full name")}
                  value={typedName}
                  onChange={(e) => setTypedName(e.target.value)}
                  sx={{ mb: 2 }}
                />

                {!otpSent ? (
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleRequestOtp}
                    disabled={!typedName.trim() || sendingOtp}
                    sx={{ textTransform: "none", fontWeight: 700, mb: 1 }}
                  >
                    {sendingOtp
                      ? t("billing.legal.sending_code", "Sending code...")
                      : t("billing.legal.send_code", "Send Verification Code")}
                  </Button>
                ) : (
                  <Stack spacing={1.5}>
                    <Alert severity="info" sx={{ py: 0.5 }}>
                      {t("billing.legal.code_sent_to", "Code sent to")} <strong>{emailHint}</strong>
                    </Alert>
                    <TextField
                      fullWidth
                      size="small"
                      label={t("billing.legal.enter_code", "Enter 6-digit code")}
                      value={otpCode}
                      onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                      placeholder="000000"
                      inputProps={{ maxLength: 6 }}
                    />
                    <Button
                      size="small"
                      onClick={handleRequestOtp}
                      disabled={sendingOtp}
                      sx={{ textTransform: "none", alignSelf: "flex-start" }}
                    >
                      {t("billing.legal.resend_code", "Resend code")}
                    </Button>
                  </Stack>
                )}

                {signError && (
                  <Typography variant="body2" color="error.main" sx={{ mt: 1.5, fontWeight: 600 }}>
                    {signError}
                  </Typography>
                )}
              </Box>
            )}
          </>
        ) : null}
      </DialogContent>

      {/* ================================================================= */}
      {/* FOOTER                                                            */}
      {/* ================================================================= */}
      <DialogActions
        sx={{
          px: 3,
          py: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: alpha(athenaColors.deepNavy, 0.5),
          borderTop: `1px solid ${alpha(athenaColors.periwinkle, 0.2)}`,
        }}
      >
        {/* Left side: scroll hint */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            opacity: hasScrolledToBottom ? 0 : 0.8,
            transition: "opacity 0.3s ease",
          }}
        >
          <ScrollIcon
            fontSize="small"
            sx={{
              color: athenaColors.periwinkle,
              animation: hasScrolledToBottom ? "none" : "bounce 1.5s infinite",
              "@keyframes bounce": {
                "0%, 100%": { transform: "translateY(0)" },
                "50%": { transform: "translateY(4px)" },
              },
            }}
          />
          <Typography variant="caption" sx={{ color: athenaColors.periwinkle }}>
            {t("billing.legal.scroll_to_continue", "Scroll to read the full document")}
          </Typography>
        </Box>

        {/* Right side: action buttons */}
        <Stack direction="row" spacing={1.5}>
          {/* Download PDF */}
          {doc.enabled && (
            <Button
              variant="outlined"
              startIcon={
                downloading ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />
              }
              onClick={handleDownload}
              disabled={downloading}
              sx={{
                borderRadius: 2,
                px: 3,
                py: 0.75,
                fontWeight: 600,
                textTransform: "none",
                fontSize: "0.88rem",
                color: athenaColors.periwinkle,
                borderColor: alpha(athenaColors.periwinkle, 0.4),
                "&:hover": {
                  borderColor: athenaColors.periwinkle,
                  background: alpha(athenaColors.periwinkle, 0.1),
                },
              }}
            >
              {t("billing.legal.download_pdf", "Download PDF")}
            </Button>
          )}

          {/* Sign / Submit Signature */}
          {doc.requires_signature && !signed && (
            <Button
              variant="contained"
              startIcon={
                signing ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <LegalIcon />
                )
              }
              onClick={showSignature ? handleSign : () => setShowSignature(true)}
              disabled={
                (showSignature && (otpCode.length !== 6 || !typedName.trim() || signing)) ||
                (!showSignature && !hasScrolledToBottom)
              }
              sx={{
                borderRadius: 2,
                px: 4,
                py: 1,
                fontWeight: 600,
                textTransform: "none",
                fontSize: "0.95rem",
                // Matches the Accept button in LegalAcceptanceModal: periwinkle
                // (#b7c1de) under a WHITE label is ~1.75:1.
                backgroundColor:
                  hasScrolledToBottom || showSignature
                    ? glass.ctaBg
                    : alpha(athenaColors.white, 0.08),
                color: theme.palette.common.white,
                border: `1px solid ${
                  hasScrolledToBottom || showSignature
                    ? glass.ctaBorder
                    : "transparent"
                }`,
                "& .MuiButton-startIcon, & .MuiButton-endIcon": { color: "inherit" },
                boxShadow:
                  hasScrolledToBottom || showSignature
                    ? `0 2px 12px ${alpha(athenaColors.royalBlue, 0.5)}`
                    : "none",
                "&:hover": {
                  backgroundColor: alpha(athenaColors.royalBlue, 0.85),
                  borderColor: athenaColors.periwinkle,
                },
                "&.Mui-disabled": {
                  backgroundColor: alpha(athenaColors.white, 0.08),
                  color: alpha(athenaColors.white, 0.45),
                },
                transition: "all 0.3s ease",
              }}
            >
              {showSignature
                ? signing
                  ? t("billing.legal.signing", "Signing...")
                  : t("billing.legal.submit_signature", "Submit Signature")
                : t("billing.legal.sign_document", "Sign Document")}
            </Button>
          )}

          {/* Signed badge */}
          {signed && (
            <Chip
              label={t("billing.legal.document_signed", "Document Signed")}
              sx={{
                height: 36,
                fontWeight: 700,
                fontSize: "0.88rem",
                color: theme.palette.common.white,
                background: theme.palette.success.light,
                borderRadius: 2,
              }}
            />
          )}
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
